/**
 * LetterComposer Component
 * Compose and send a physical letter to a member via print-and-mail integration.
 */

import React, { useState, useEffect } from 'react';
import { Mail, MapPin, CheckCircle, AlertCircle, Loader2, FileText, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import lettersApi, { LetterTemplate, LetterSend } from '@/services/lettersApi';

// ============================================================================
// TYPES
// ============================================================================

interface LetterComposerProps {
  memberId?: number;
  memberName?: string;
  memberAddress?: {
    street_address?: string;
    city?: string;
    state?: string;
    zip_code?: string;
  };
  programId?: number;
  enrollmentId?: number;
  /** TRUS-762: file the letter under this case. */
  caseEpisodeId?: number | null;
  onClose: () => void;
  onSent?: (send: any) => void;
  /**
   * 'dialog' (default) or 'page': the same composer rendered in place, without
   * its own title, for a host screen that has a header and a way back
   * (the member's Activities workspace, TRUS-762).
   */
  variant?: 'dialog' | 'page';
}

interface MergeField {
  key: string;
  value: string;
}

// ============================================================================
// COMPONENT
// ============================================================================

export const LetterComposer: React.FC<LetterComposerProps> = ({
  memberId,
  memberName,
  memberAddress,
  programId,
  enrollmentId,
  caseEpisodeId,
  onClose,
  onSent,
  variant = 'dialog',
}) => {
  /** Dialog chrome, or none: in page mode the host screen owns title and Back. */
  const shell = (title: React.ReactNode, contentClass: string, body: React.ReactNode) =>
    variant === 'page' ? (
      <div className="rounded-lg border border-v2border bg-surface p-4 sm:p-6">{body}</div>
    ) : (
      <Dialog open onOpenChange={() => onClose()}>
        <DialogContent className={contentClass}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">{title}</DialogTitle>
          </DialogHeader>
          {body}
        </DialogContent>
      </Dialog>
    );
  // Templates
  const [templates, setTemplates] = useState<LetterTemplate[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');

  // Form fields
  const [recipientName, setRecipientName] = useState(memberName || '');
  const [addressLine1, setAddressLine1] = useState(memberAddress?.street_address || '');
  const [addressLine2, setAddressLine2] = useState('');
  const [city, setCity] = useState(memberAddress?.city || '');
  const [state, setState] = useState(memberAddress?.state || '');
  const [zip, setZip] = useState(memberAddress?.zip_code || '');

  // Mail options
  const [mailType, setMailType] = useState<'usps_first_class' | 'usps_standard'>('usps_first_class');
  const [color, setColor] = useState(false);
  const [doubleSided, setDoubleSided] = useState(false);

  // Merge data
  const [mergeFields, setMergeFields] = useState<MergeField[]>([]);

  // Address verification
  const [verifying, setVerifying] = useState(false);
  const [addressVerified, setAddressVerified] = useState(false);
  const [verificationResult, setVerificationResult] = useState<string | null>(null);

  // Sending
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sentLetter, setSentLetter] = useState<LetterSend | null>(null);

  // --------------------------------------------------------------------------
  // Load templates on mount
  // --------------------------------------------------------------------------

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoadingTemplates(true);
        const data = await lettersApi.listTemplates();
        if (!cancelled) {
          setTemplates(data.filter((t) => t.is_active));
        }
      } catch (err: any) {
        if (!cancelled) {
          setError('Failed to load letter templates.');
        }
      } finally {
        if (!cancelled) setLoadingTemplates(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  // When a template is selected, pre-populate merge fields from its merge_fields list
  useEffect(() => {
    if (!selectedTemplateId) return;
    const template = templates.find((t) => t.id === Number(selectedTemplateId));
    if (template?.merge_fields?.length) {
      setMergeFields(
        template.merge_fields.map((key) => ({
          key,
          value: '',
        }))
      );
    } else {
      setMergeFields([]);
    }
  }, [selectedTemplateId, templates]);

  // --------------------------------------------------------------------------
  // Address verification
  // --------------------------------------------------------------------------

  const handleVerifyAddress = async () => {
    if (!addressLine1 || !city || !state || !zip) {
      setError('Please fill in all required address fields before verifying.');
      return;
    }
    try {
      setVerifying(true);
      setError(null);
      setVerificationResult(null);
      const result = await lettersApi.verifyAddress({
        address_line1: addressLine1,
        address_line2: addressLine2 || undefined,
        city,
        state,
        zip_code: zip,
      });
      if (result.verified) {
        setAddressVerified(true);
        setVerificationResult(result.standardized || 'Address verified successfully.');
      } else {
        setAddressVerified(false);
        const msg = result.errors?.join(', ') || `Deliverability: ${result.deliverability || 'unknown'}`;
        setVerificationResult(`Address could not be verified. ${msg}`);
      }
    } catch (err: any) {
      setAddressVerified(false);
      setVerificationResult(null);
      setError(err?.response?.data?.detail || 'Address verification failed.');
    } finally {
      setVerifying(false);
    }
  };

  // Reset verification when address changes
  useEffect(() => {
    setAddressVerified(false);
    setVerificationResult(null);
  }, [addressLine1, addressLine2, city, state, zip]);

  // --------------------------------------------------------------------------
  // Send letter
  // --------------------------------------------------------------------------

  const handleSend = async () => {
    if (!selectedTemplateId) {
      setError('Please select a letter template.');
      return;
    }
    if (!recipientName) {
      setError('Recipient name is required.');
      return;
    }
    if (!addressLine1 || !city || !state || !zip) {
      setError('Please fill in all required address fields.');
      return;
    }

    const mergeData: Record<string, string> = {};
    for (const field of mergeFields) {
      if (field.key && field.value) {
        mergeData[field.key] = field.value;
      }
    }

    try {
      setSending(true);
      setError(null);
      const result = await lettersApi.sendLetter({
        member_id: memberId,
        template_id: Number(selectedTemplateId),
        recipient_name: recipientName,
        recipient_address_line1: addressLine1,
        recipient_address_line2: addressLine2 || undefined,
        recipient_city: city,
        recipient_state: state,
        recipient_zip: zip,
        merge_data: Object.keys(mergeData).length > 0 ? mergeData : undefined,
        mail_type: mailType,
        color,
        double_sided: doubleSided,
        program_id: programId,
        enrollment_id: enrollmentId,
        ...(caseEpisodeId ? { case_episode_id: caseEpisodeId } : {}),
      });
      setSentLetter(result);
      onSent?.(result);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to send letter. Please try again.');
    } finally {
      setSending(false);
    }
  };

  // --------------------------------------------------------------------------
  // Merge field helpers
  // --------------------------------------------------------------------------

  const addMergeField = () => {
    setMergeFields((prev) => [...prev, { key: '', value: '' }]);
  };

  const updateMergeField = (index: number, field: 'key' | 'value', val: string) => {
    setMergeFields((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: val };
      return updated;
    });
  };

  const removeMergeField = (index: number) => {
    setMergeFields((prev) => prev.filter((_, i) => i !== index));
  };

  // --------------------------------------------------------------------------
  // Render: Success state
  // --------------------------------------------------------------------------

  if (sentLetter) {
    return shell(
      <>
              <CheckCircle className="h-5 w-5 text-green-600" />
              Letter Sent Successfully
      </>,
      'sm:max-w-lg',
          <div className="space-y-4 py-2">
            <Card>
              <CardContent className="pt-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Status</span>
                  <Badge variant="outline" className="capitalize">
                    {sentLetter.status}
                  </Badge>
                </div>
                {sentLetter.template_name && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Template</span>
                    <span className="text-sm font-medium">{sentLetter.template_name}</span>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Recipient</span>
                  <span className="text-sm font-medium">{sentLetter.recipient_name}</span>
                </div>
                {sentLetter.external_send_id && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Tracking ID</span>
                    <span className="text-sm font-mono">{sentLetter.external_send_id}</span>
                  </div>
                )}
                {sentLetter.expected_delivery_date && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Expected Delivery</span>
                    <span className="text-sm font-medium">
                      {new Date(sentLetter.expected_delivery_date).toLocaleDateString()}
                    </span>
                  </div>
                )}
                {sentLetter.tracking_url && (
                  <div className="pt-2">
                    <a
                      href={sentLetter.tracking_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:underline"
                    >
                      View Tracking Details
                    </a>
                  </div>
                )}
              </CardContent>
            </Card>
            <div className="flex justify-end">
              <Button onClick={onClose}>Close</Button>
            </div>
          </div>,
    );
  }

  // --------------------------------------------------------------------------
  // Render: Compose form
  // --------------------------------------------------------------------------

  return shell(
    <>
            <Mail className="h-5 w-5" />
            Send Letter
    </>,
    'sm:max-w-2xl max-h-[90vh] overflow-y-auto',
        <div className="space-y-6 py-2">
          {/* Error banner */}
          {error && (
            <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>{error}</span>
              <button onClick={() => setError(null)} className="ml-auto">
                <X className="h-4 w-4" />
              </button>
            </div>
          )}

          {/* Template selector */}
          <div className="space-y-2">
            <Label htmlFor="template">Letter Template</Label>
            {loadingTemplates ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading templates...
              </div>
            ) : (
              <Select value={selectedTemplateId} onValueChange={setSelectedTemplateId}>
                <SelectTrigger id="template">
                  <SelectValue placeholder="Select a template" />
                </SelectTrigger>
                <SelectContent>
                  {templates.map((t) => (
                    <SelectItem key={t.id} value={String(t.id)}>
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-muted-foreground" />
                        {t.template_name}
                        <Badge variant="secondary" className="ml-1 text-xs capitalize">
                          {t.template_type}
                        </Badge>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Recipient name */}
          <div className="space-y-2">
            <Label htmlFor="recipientName">Recipient Name</Label>
            <Input
              id="recipientName"
              placeholder="Full name"
              value={recipientName}
              onChange={(e) => setRecipientName(e.target.value)}
            />
          </div>

          {/* Address fields */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <MapPin className="h-4 w-4" />
                Mailing Address
                {addressVerified && (
                  <Badge variant="outline" className="text-green-700 border-green-300 bg-green-50 ml-2">
                    <CheckCircle className="h-3 w-3 mr-1" />
                    Verified
                  </Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="space-y-2">
                <Label htmlFor="addressLine1">Address Line 1</Label>
                <Input
                  id="addressLine1"
                  placeholder="Street address"
                  value={addressLine1}
                  onChange={(e) => setAddressLine1(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="addressLine2">Address Line 2 (optional)</Label>
                <Input
                  id="addressLine2"
                  placeholder="Apt, suite, unit, etc."
                  value={addressLine2}
                  onChange={(e) => setAddressLine2(e.target.value)}
                />
              </div>
              <div className="grid grid-cols-6 gap-3">
                <div className="col-span-3 space-y-2">
                  <Label htmlFor="city">City</Label>
                  <Input
                    id="city"
                    placeholder="City"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                  />
                </div>
                <div className="col-span-1 space-y-2">
                  <Label htmlFor="state">State</Label>
                  <Input
                    id="state"
                    placeholder="CA"
                    maxLength={2}
                    value={state}
                    onChange={(e) => setState(e.target.value.toUpperCase())}
                  />
                </div>
                <div className="col-span-2 space-y-2">
                  <Label htmlFor="zip">ZIP Code</Label>
                  <Input
                    id="zip"
                    placeholder="00000"
                    maxLength={10}
                    value={zip}
                    onChange={(e) => setZip(e.target.value)}
                  />
                </div>
              </div>

              {/* Verification result */}
              {verificationResult && (
                <div
                  className={`text-sm rounded-md p-2 ${
                    addressVerified
                      ? 'bg-green-50 text-green-800 border border-green-200'
                      : 'bg-yellow-50 text-yellow-800 border border-yellow-200'
                  }`}
                >
                  {verificationResult}
                </div>
              )}

              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleVerifyAddress}
                disabled={verifying || !addressLine1 || !city || !state || !zip}
              >
                {verifying ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  <>
                    <MapPin className="h-4 w-4 mr-1" />
                    Verify Address
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Merge data fields */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>Merge Data (optional)</Label>
              <Button type="button" variant="ghost" size="sm" onClick={addMergeField}>
                + Add Field
              </Button>
            </div>
            {mergeFields.length > 0 && (
              <div className="space-y-2">
                {mergeFields.map((field, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <Input
                      placeholder="Key"
                      value={field.key}
                      onChange={(e) => updateMergeField(index, 'key', e.target.value)}
                      className="flex-1"
                    />
                    <Input
                      placeholder="Value"
                      value={field.value}
                      onChange={(e) => updateMergeField(index, 'value', e.target.value)}
                      className="flex-1"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeMergeField(index)}
                      className="shrink-0"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Mail options */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Mail Options</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="mailType">Mail Type</Label>
                <Select
                  value={mailType}
                  onValueChange={(val) => setMailType(val as 'usps_first_class' | 'usps_standard')}
                >
                  <SelectTrigger id="mailType">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="usps_first_class">First Class</SelectItem>
                    <SelectItem value="usps_standard">Standard</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="color">Color Printing</Label>
                  <p className="text-xs text-muted-foreground">Print in full color instead of black and white</p>
                </div>
                <Switch id="color" checked={color} onCheckedChange={setColor} />
              </div>
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="doubleSided">Double-Sided</Label>
                  <p className="text-xs text-muted-foreground">Print on both sides of the page</p>
                </div>
                <Switch id="doubleSided" checked={doubleSided} onCheckedChange={setDoubleSided} />
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={sending}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSend}
              disabled={sending || !selectedTemplateId || !recipientName || !addressLine1 || !city || !state || !zip}
            >
              {sending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Mail className="h-4 w-4 mr-2" />
                  Send Letter
                </>
              )}
            </Button>
          </div>
        </div>,
  );
};

export default LetterComposer;
